import {
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { Table, Button, Select, Space, Modal, message } from "antd";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "../../../shared/styles/ErpPages.css";
import { formatCurrency } from "../../../utils/formatters";
import { invalidateCatalog, useCustomersCatalog } from "../../../hooks/useCatalogData";
import usePermissions from "../../../hooks/usePermissions";
import { filterBySearch } from "../../../shared/lib/listSearch";
import ListSearch from "../../../shared/ui/ListSearch";
import { apiClient } from "../../../shared/api/AllApi";

/**
 * ERP customer index — View / Edit / soft-Delete.
 */
const GetCustomer = () => {
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("debt");
  const [rows, setRows] = useState([]);
  const { data: customers, refresh, loading } = useCustomersCatalog();

  useEffect(() => {
    setRows(
      (customers || []).map((c) => ({
        id: c.id,
        name: c.name || "",
        phone: c.phone_number || "",
        email: c.email || "",
        type: c.type || "",
        owed: parseFloat(c.total_owed || 0),
      }))
    );
  }, [customers]);

  const counts = useMemo(() => {
    const withDebt = rows.filter((r) => r.owed > 0.0001);
    return {
      all: rows.length,
      debt: withDebt.length,
      clear: rows.length - withDebt.length,
      debtTotal: withDebt.reduce((s, r) => s + r.owed, 0),
    };
  }, [rows]);

  const list = useMemo(() => {
    const scoped = rows.filter((r) => {
      if (filter === "debt" && r.owed <= 0.0001) return false;
      if (filter === "clear" && r.owed > 0.0001) return false;
      return true;
    });
    return filterBySearch(scoped, search, (r) => [
      r.id,
      r.name,
      r.phone,
      r.email,
      r.type,
      r.owed,
    ]);
  }, [rows, search, filter]);

  const typeLabel = (type) => {
    if (type === "individual") return t("individual", "Individual");
    if (type === "company") return t("company", "Company");
    if (type === "organization") return t("organization", "Organization");
    return type || "—";
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: t("customerDeleteConfirm", "Archive this customer?"),
      content: t(
        "customerSoftDeleteHint",
        "This is a soft delete. Orders and payments stay in history."
      ),
      okType: "danger",
      okText: t("delete", "Delete"),
      cancelText: t("cancel", "Cancel"),
      onOk: async () => {
        try {
          await apiClient.delete(`/customers/${record.id}`);
          message.success(t("customerDeleted", "Customer archived."));
          invalidateCatalog("customers");
          refresh?.();
        } catch (e) {
          message.error(e?.response?.data?.message || t("customerDeleteFailed", "Delete failed"));
        }
      },
    });
  };

  return (
    <div className="erp-page">
      <div className="erp-shell">
        <div className="erp-header">
          <div>
            <div className="erp-breadcrumb">
              {t("sales", "Sales")} / {t("customers", "Customers")}
            </div>
            <h1>{t("customers", "Customers")}</h1>
          </div>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                invalidateCatalog("customers");
                refresh?.();
              }}
            >
              {t("refresh", "Refresh")}
            </Button>
            {hasPermission("customers.add") && (
              <Link to="/AddCustomer">
                <Button type="primary" icon={<PlusOutlined />}>
                  {t("addCustomer", "New customer")}
                </Button>
              </Link>
            )}
          </Space>
        </div>

        <div className="erp-meta">
          <span>
            {t("records", "Records")}: <b>{counts.all}</b>
          </span>
          <span>
            {t("withBalance", "With balance")}: <b>{counts.debt}</b>
          </span>
          <span>
            {t("openReceivables", "Open receivables")}:{" "}
            <b className="erp-money debt">{formatCurrency(counts.debtTotal)}</b>
          </span>
        </div>

        <div className="erp-toolbar">
          <Select
            value={filter}
            onChange={setFilter}
            style={{ width: 200 }}
            options={[
              { value: "debt", label: `${t("withBalance", "With balance")} (${counts.debt})` },
              { value: "clear", label: `${t("zeroBalance", "Zero balance")} (${counts.clear})` },
              { value: "all", label: `${t("all", "All")} (${counts.all})` },
            ]}
          />
          <ListSearch
            value={search}
            onChange={setSearch}
            placeholder={t("searchCustomerPlaceholder", "Code / name / phone")}
            width={280}
          />
        </div>

        <div className="erp-body">
          <Table
            size="small"
            rowKey="id"
            loading={loading}
            dataSource={list}
            scroll={{ x: "max-content" }}
            pagination={{
              pageSize: 50,
              showSizeChanger: true,
              showTotal: (total) => `${total} ${t("records", "records")}`,
            }}
            locale={{ emptyText: t("noDataFound", "No records") }}
            columns={[
              {
                title: t("id", "ID"),
                dataIndex: "id",
                width: 70,
                sorter: (a, b) => a.id - b.id,
              },
              {
                title: t("name", "Name"),
                dataIndex: "name",
                sorter: (a, b) => a.name.localeCompare(b.name),
                render: (v, r) => (
                  <Button
                    type="link"
                    style={{ padding: 0, height: "auto", fontWeight: 600 }}
                    onClick={() => navigate(`/GetCustomer/view/${r.id}`)}
                  >
                    {v || `Customer #${r.id}`}
                  </Button>
                ),
              },
              {
                title: t("phoneNumber", "Phone"),
                dataIndex: "phone",
                width: 140,
                render: (v) => v || "—",
              },
              {
                title: t("type", "Type"),
                dataIndex: "type",
                width: 120,
                render: (v) => typeLabel(v),
              },
              {
                title: t("balance", "Balance"),
                dataIndex: "owed",
                width: 150,
                align: "right",
                sorter: (a, b) => a.owed - b.owed,
                defaultSortOrder: "descend",
                render: (owed) =>
                  owed > 0.0001 ? (
                    <span className="erp-money debt">{formatCurrency(owed)}</span>
                  ) : (
                    <span className="erp-money ok">0</span>
                  ),
              },
              {
                title: t("status", "Status"),
                key: "status",
                width: 110,
                render: (_, r) =>
                  r.owed > 0.0001 ? (
                    <span className="erp-status debt">{t("open", "Open")}</span>
                  ) : (
                    <span className="erp-status clear">{t("settled", "Settled")}</span>
                  ),
              },
              {
                title: t("action", "Action"),
                key: "action",
                width: 220,
                fixed: "right",
                render: (_, r) => (
                  <Space size={4} wrap>
                    <Button
                      size="small"
                      type="primary"
                      icon={<EyeOutlined />}
                      onClick={() => navigate(`/GetCustomer/view/${r.id}`)}
                    >
                      {t("view", "View")}
                    </Button>
                    {hasPermission("customers.edit") && (
                      <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => navigate(`/GetCustomer/UpdateCustomer/${r.id}`)}
                      >
                        {t("edit", "Edit")}
                      </Button>
                    )}
                    {hasPermission("customers.delete") && (
                      <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(r)}
                      >
                        {t("delete", "Delete")}
                      </Button>
                    )}
                  </Space>
                ),
              },
            ]}
          />
        </div>
      </div>
    </div>
  );
};

export default GetCustomer;
